import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { getCompanyFinancials, getDraftCompanyFinancials } from '$/store/portfolio/actions';
import FinancialStatement from '$/components/financialStatement';
import { AnalyticsService } from '$/services';
import './index.scss';

interface ICompanyKpisProps {
  currentCompany?: Bixby.Models.Company;
  financialStatements: Bixby.Models.FinancialStatement[];
  profile?: Bixby.Models.Profile;
  financialsError?: any;
  portfolioIsLoading?: boolean;
  dispatchGetCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => Promise<void>;
  dispatchGetDraftCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => Promise<void>;
}

const CompanyKpisProps: React.FC<ICompanyKpisProps> = props => {
  const {
    currentCompany,
    financialStatements,
    profile,
    dispatchGetCompanyFinancial,
    dispatchGetDraftCompanyFinancial,
    financialsError,
    portfolioIsLoading,
  } = props;
  const getKpis: (currentCompany?: Bixby.Models.Company) => string[][] = (currentCompany): string[][] => {
    let result: string[][] = [];
    if (!currentCompany || currentCompany.kpiState !== Bixby.Enums.KpiState.KpisVisible) {
      return result;
    }

    const statement = Bixby.Utils.find(financialStatements, item => {
      return (
        item.financialStatementType === Bixby.Enums.FinancialStatementType.KPIs &&
        item.isDraft === currentCompany.isDraft
      );
    });

    if (statement) {
      result = Object.assign([], statement.data || []);
    }

    return result;
  };

  const statementData = useMemo(() => {
    const statement = Bixby.Utils.find(financialStatements, item => {
      return (
        item.financialStatementType === Bixby.Enums.FinancialStatementType.KPIs &&
        item.isDraft === currentCompany?.isDraft
      );
    });
    return statement?.data;
  }, [financialStatements, currentCompany]);

  const tryGetKpis = useCallback(
    (currentCompany: Bixby.Models.Company) => {
      const dispatchGetFinancials = currentCompany.isDraft
        ? dispatchGetDraftCompanyFinancial
        : dispatchGetCompanyFinancial;

      if (!statementData) {
        dispatchGetFinancials(currentCompany, Bixby.Enums.FinancialStatementType.KPIs);
      }
    },
    [dispatchGetCompanyFinancial, dispatchGetDraftCompanyFinancial, statementData]
  );

  useEffect(() => {
    if (
      currentCompany &&
      currentCompany.kpiState === Bixby.Enums.KpiState.KpisVisible &&
      !currentCompany.doesNotHaveFinancials
    ) {
      if (portfolioIsLoading === false && !financialsError) {
        tryGetKpis(currentCompany);
      }
    }
  }, [currentCompany, tryGetKpis, financialsError, portfolioIsLoading]);

  let kpisFeedbackUrl = '';

  if (currentCompany) {
    const companyNameSanitized: string = (currentCompany.name || '').replace('&', '%26');
    if (currentCompany.kpiState === Bixby.Enums.KpiState.KpisVisible) {
      const dataSiteNameSanitized: string = (currentCompany.dataSiteName || '').replace('&', '%26');
      const feedbackUrlSubject = `KPIs%20Request%20-%20${companyNameSanitized}`;
      const dataSite: string = dataSiteNameSanitized ? `%20%28${dataSiteNameSanitized}%29` : '';
      const feedbackUrlBody = `Please%20add%20KPIs%20for%20${companyNameSanitized}${dataSite}%2c%20and%20include%20any%20specific%20requests%20here%3A%0D%0A%0D%0A`;
      kpisFeedbackUrl = `mailto:feedback@bixbyresearch.com?subject=${feedbackUrlSubject}&body=${feedbackUrlBody}`;
    } else if (
      currentCompany.kpiState === Bixby.Enums.KpiState.KpisNotVisible ||
      currentCompany.kpiState === Bixby.Enums.KpiState.KpisNotYetReviewed
    ) {
      const feedbackUrlSubject = `Hi%20-%20I%20would%20like%20to%20inquire%20about%20KPIs%20for%20${companyNameSanitized}`;
      kpisFeedbackUrl = `mailto:feedback@bixbyresearch.com?subject=${feedbackUrlSubject}`;
    }
  }

  const handleFeedbackClick = () => {
    if (profile && currentCompany) {
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_FEEDBACK_LINK_CLICK, {
        ...profile,
        type: 'KPIs',
        companyName: currentCompany.name,
      });
    }
  };

  let isLoading = false;
  if (currentCompany) {
    const statement = Bixby.Utils.find(financialStatements, item => {
      return (
        item.financialStatementType === Bixby.Enums.FinancialStatementType.KPIs &&
        item.isDraft === currentCompany.isDraft
      );
    });

    if (statement) {
      isLoading = statement.isLoading;
    }
  }

  return (
    <React.Fragment>
      <section>
        <div className="kpis">
          KPIs
          {currentCompany && currentCompany.kpiState === Bixby.Enums.KpiState.KpisVisible && (
            <div className="kpis-note">
              <p>
                Don't see all the KPIs you're looking for?{' '}
                <a href={kpisFeedbackUrl} onClick={handleFeedbackClick}>
                  Click here
                </a>{' '}
                and we can populate KPIs.
              </p>
            </div>
          )}
          {currentCompany && currentCompany.kpiState === Bixby.Enums.KpiState.KpisNotYetReviewed && (
            <div className="kpis-note">
              <p>
                Data site reviewed by Bixby and no KPIs available, please{' '}
                <a href={kpisFeedbackUrl} onClick={handleFeedbackClick}>
                  contact us
                </a>{' '}
                with any questions.
              </p>
            </div>
          )}
          {currentCompany && currentCompany.kpiState === Bixby.Enums.KpiState.KpisNotVisible && (
            <div className="kpis-note">
              <p>
                Bixby has yet to review the data site for KPIs, please{' '}
                <a href={kpisFeedbackUrl} onClick={handleFeedbackClick}>
                  contact us
                </a>{' '}
                to have it reviewed.
              </p>
            </div>
          )}
        </div>
        {currentCompany && currentCompany.kpiState === Bixby.Enums.KpiState.KpisVisible && (
          <div className="financial_table green_sheet_wrap">
            <FinancialStatement companyFinancial={getKpis(currentCompany) || []} isLoading={isLoading} />
          </div>
        )}
      </section>
    </React.Fragment>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    currentCompany: state.portfolioState.currentCompany,
    financialStatements: state.portfolioState.financialStatements,
    profile: state.profileState.profile,
    financialsError: state.portfolioState.error,
    portfolioIsLoading: state.portfolioState.isLoading,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCompanyFinancial: (company: Bixby.Models.Company, statementType: Bixby.Enums.FinancialStatementType) =>
    dispatch(getCompanyFinancials(company, statementType)),
  dispatchGetDraftCompanyFinancial: (
    company: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ) => dispatch(getDraftCompanyFinancials(company, statementType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyKpisProps);
